// Update imports to add necessary components
import { useMsal } from '@azure/msal-react';
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Alert,
  AlertTitle,
  Container,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Avatar,
  ListItemText,
  ListItemAvatar
} from "@mui/material";

// Import icons (add any needed)
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import CodeIcon from "@mui/icons-material/Code";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RestoreIcon from "@mui/icons-material/Restore";
import CloseIcon from "@mui/icons-material/Close";
import BackupIcon from "@mui/icons-material/Backup";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Client } from '@microsoft/microsoft-graph-client';
import SearchIcon from "@mui/icons-material/Search";
import AppIcon from "@mui/icons-material/Apps";
import WebIcon from "@mui/icons-material/Web";
import MobileScreenShareIcon from "@mui/icons-material/MobileScreenShare";
import ApiIcon from "@mui/icons-material/Api";
import SecurityIcon from "@mui/icons-material/Security";

// Updated interface for props
interface ManifestEditorProps {
  applications: any[]; // Array of all applications
  onBack?: () => void;
  onSave?: (manifest: any) => Promise<any>;
  onSelectApp?: (app: any) => void;
}

// Enhanced interface for application with search metadata
interface SearchableApplication {
  id: string;
  appId: string;
  displayName: string;
  signInAudience: string;
  appType: 'Web' | 'SPA' | 'API' | 'Mobile' | 'Unknown';
  hasSecrets: boolean;
  hasCredentials: boolean;
  tags: string[];
  searchText: string; // Combined search text for better matching
}

const ManifestEditor: React.FC<ManifestEditorProps> = ({ 
  applications, 
  onBack,
  onSave,
  onSelectApp
}) => {
  const { instance, accounts } = useMsal();
  // State for manifest JSON
  const [manifestJson, setManifestJson] = useState<string>("");
  const [manifestObject, setManifestObject] = useState<any>(null);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // State for selected application
  const [selectedAppId, setSelectedAppId] = useState<string>("");
  const [selectedApp, setSelectedApp] = useState<any>(null);

  // New state for enhanced search
  const [searchableApps, setSearchableApps] = useState<SearchableApplication[]>([]);
  const [selectedSearchApp, setSelectedSearchApp] = useState<SearchableApplication | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchTips, setShowSearchTips] = useState(false);

  const getGraphClient = useCallback(async () => {
    if (!accounts || accounts.length === 0) {
      throw new Error("No active account found. Please sign in again.");
    }

    try {
      // Acquire token directly from MSAL
      const response = await instance.acquireTokenSilent({
        scopes: ["Application.ReadWrite.All"],
        account: accounts[0]
      });

      // Initialize and return Graph client with the token
      return Client.init({
        authProvider: (done) => {
          done(null, response.accessToken);
        }
      });
    } catch (error: any) {
      console.error("Token acquisition failed:", error);
      
      if (error.name === "InteractionRequiredAuthError") {
        try {
          // Fallback to popup for interactive sign-in
          const response = await instance.acquireTokenPopup({
            scopes: ["Application.ReadWrite.All"],
            account: accounts[0]
          });
          
          return Client.init({
            authProvider: (done) => {
              done(null, response.accessToken);
            }
          });
        } catch (popupError) {
          throw new Error("Failed to authenticate. Please sign in again.");
        }
      }
      
      throw new Error("Authentication failed. Please try again.");
    }
  }, [instance, accounts]);

  // Process applications for enhanced search
  useEffect(() => {
    const processApplications = () => {
      const processed = applications.map(app => {
        // Determine app type based on configuration
        let appType: 'Web' | 'SPA' | 'API' | 'Mobile' | 'Unknown' = 'Unknown';
        
        if (app.web?.redirectUris?.length > 0) {
          appType = 'Web';
        } else if (app.spa?.redirectUris?.length > 0) {
          appType = 'SPA';
        } else if (app.api?.oauth2PermissionScopes?.length > 0 || app.appRoles?.length > 0) {
          appType = 'API';
        } else if (app.publicClient?.redirectUris?.length > 0) {
          appType = 'Mobile';
        }

        // Create comprehensive search text
        const searchText = [
          app.displayName || '',
          app.appId || '',
          app.signInAudience || '',
          appType,
          ...(app.tags || []),
          ...(app.identifierUris || [])
        ].join(' ').toLowerCase();

        return {
          id: app.id,
          appId: app.appId || '',
          displayName: app.displayName || 'Unnamed Application',
          signInAudience: app.signInAudience || 'Unknown',
          appType,
          hasSecrets: (app.passwordCredentials?.length || 0) > 0,
          hasCredentials: (app.keyCredentials?.length || 0) > 0,
          tags: app.tags || [],
          searchText
        } as SearchableApplication;
      });

      // Sort alphabetically by display name
      processed.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setSearchableApps(processed);
    };

    if (applications.length > 0) {
      processApplications();
    }
  }, [applications]);

  // Get app type icon
  const getAppTypeIcon = (appType: string) => {
    switch (appType) {
      case 'Web':
        return <WebIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
      case 'SPA':
        return <MobileScreenShareIcon sx={{ fontSize: 16, color: '#9c27b0' }} />;
      case 'API':
        return <ApiIcon sx={{ fontSize: 16, color: '#f57c00' }} />;
      case 'Mobile':
        return <MobileScreenShareIcon sx={{ fontSize: 16, color: '#388e3c' }} />;
      default:
        return <AppIcon sx={{ fontSize: 16, color: '#757575' }} />;
    }
  };

  // Get audience color
  const getAudienceColor = (audience: string) => {
    switch (audience) {
      case 'AzureADMyOrg':
        return '#1976d2';
      case 'AzureADMultipleOrgs':
        return '#f57c00';
      case 'AzureADandPersonalMicrosoftAccount':
        return '#9c27b0';
      case 'PersonalMicrosoftAccount':
        return '#388e3c';
      default:
        return '#757575';
    }
  };

  // Enhanced application selection handler
  const handleEnhancedApplicationChange = async (
    event: React.SyntheticEvent,
    newValue: SearchableApplication | null
  ) => {
    if (!newValue) {
      setSelectedSearchApp(null);
      setSelectedApp(null);
      setSelectedAppId('');
      setManifestJson('');
      setManifestObject(null);
      return;
    }

    setSearchLoading(true);
    setSelectedSearchApp(newValue);
    setSelectedAppId(newValue.id);
    setError('');
    
    try {
      // Find the full application object
      const basicApp = applications.find(a => a.id === newValue.id);
      if (basicApp) {
        setSelectedApp(basicApp);
        
        // Fetch the complete manifest from Graph API
        const client = await getGraphClient();
        
        console.log(`📋 Manifest: Loading complete manifest for ${newValue.displayName}...`);
        
        const fullAppResponse = await client.api(`/applications/${newValue.id}`)
          .select([
            'id',
            'appId',
            'displayName',
            'identifierUris',
            'web',
            'spa',
            'publicClient',
            'api',
            'appRoles',
            'requiredResourceAccess',
            'passwordCredentials',
            'keyCredentials',
            'signInAudience',
            'groupMembershipClaims',
            'optionalClaims',
            'oauth2RequirePostResponse',
            'tags',
            'tokenEncryptionKeyId',
            'isFallbackPublicClient',
            'isDeviceOnlyAuthSupported',
            'notes',
            'defaultRedirectUri',
            'description'
          ].join(','))
          .get();

        console.log(`✅ Manifest: Complete manifest loaded for ${newValue.displayName}`);
        
        // Create filtered response (excluding read-only properties)
        const filteredResponse = {
          id: fullAppResponse.id,
          appId: fullAppResponse.appId,
          displayName: fullAppResponse.displayName,
          identifierUris: fullAppResponse.identifierUris,
          web: fullAppResponse.web,
          spa: fullAppResponse.spa,
          publicClient: fullAppResponse.publicClient,
          api: fullAppResponse.api,
          appRoles: fullAppResponse.appRoles,
          requiredResourceAccess: fullAppResponse.requiredResourceAccess,
          passwordCredentials: fullAppResponse.passwordCredentials,
          keyCredentials: fullAppResponse.keyCredentials,
          signInAudience: fullAppResponse.signInAudience,
          groupMembershipClaims: fullAppResponse.groupMembershipClaims,
          optionalClaims: fullAppResponse.optionalClaims,
          oauth2RequirePostResponse: fullAppResponse.oauth2RequirePostResponse,
          tags: fullAppResponse.tags,
          tokenEncryptionKeyId: fullAppResponse.tokenEncryptionKeyId,
          isFallbackPublicClient: fullAppResponse.isFallbackPublicClient,
          isDeviceOnlyAuthSupported: fullAppResponse.isDeviceOnlyAuthSupported,
          notes: fullAppResponse.notes,
          defaultRedirectUri: fullAppResponse.defaultRedirectUri,
          description: fullAppResponse.description
        };

        setSelectedApp(filteredResponse);
        
        // Format as pretty JSON
        const prettyJson = JSON.stringify(filteredResponse, null, 2);
        setManifestJson(prettyJson);
        setManifestObject(filteredResponse);
        
        if (onSelectApp) {
          onSelectApp(filteredResponse);
        }
      }
    } catch (err: any) {
      console.error('❌ Manifest: Error fetching complete application manifest:', err);
      setError(`Failed to retrieve complete application manifest: ${err.message || 'Unknown error'}`);
      
      // Fallback to basic app data
      const basicApp = applications.find(a => a.id === newValue.id);
      if (basicApp) {
        setSelectedApp(basicApp);
        setManifestJson(JSON.stringify(basicApp, null, 2));
        setManifestObject(basicApp);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  // Custom filter function for better search
  const filterApplications = (options: SearchableApplication[], { inputValue }: { inputValue: string }) => {
    if (!inputValue.trim()) {
      return options.slice(0, 50); // Show first 50 apps when no search
    }

    const searchTerms = inputValue.toLowerCase().split(' ').filter(term => term.length > 0);
    
    return options.filter(app => {
      return searchTerms.every(term => app.searchText.includes(term));
    }).slice(0, 100); // Limit results to 100 for performance
  };  

  // Parse the manifest from JSON
  const parseManifest = () => {
    try {
      if (!manifestJson.trim()) {
        setError("Manifest JSON cannot be empty.");
        setManifestObject(null);
        return false;
      }
      
      const parsed = JSON.parse(manifestJson);
      setManifestObject(parsed);
      setError("");
      return true;
    } catch (err: any) {
      setError(`JSON parsing error: ${err.message}`);
      return false;
    }
  };

  // Validate and prepare for saving
  const handleValidate = () => {
    const isValid = parseManifest();
    if (isValid) {
      setSuccessMessage("Manifest JSON is valid.");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  // Initiate save process
  const handleSaveClick = () => {
    const isValid = parseManifest();
    if (isValid) {
      setConfirmOpen(true);
    }
  };

  // Update handleConfirmSave to ensure assignedPlans doesn't get sent back
  const handleConfirmSave = async () => {
    if (!onSave || !manifestObject) return;
    
    setIsLoading(true);
    setConfirmOpen(false);
    
    try {
      // Create a copy of the manifest object and remove assignedPlans if present
      const manifestToSave = { ...manifestObject };
      if (manifestToSave.assignedPlans) {
        delete manifestToSave.assignedPlans;
      }
      
      await onSave(manifestToSave);
      setSuccessMessage("Manifest saved successfully.");
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (err: any) {
      setError(`Failed to save manifest: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle copy to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(manifestJson);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Reset manifest to original
  const handleReset = () => {
    if (selectedApp) {
      const prettyJson = JSON.stringify(selectedApp, null, 2);
      setManifestJson(prettyJson);
      setManifestObject(selectedApp);
      setError("");
      setSuccessMessage("Manifest reset to original version.");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          {onBack && (
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                mr: 2,
                borderRadius: 2,
                pl: 1.5,
                color: "text.secondary",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  borderColor: "primary.main",
                  color: "primary.main"
                }
              }}
            >
              Back
            </Button>
          )}
          
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              Application Manifest Editor
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Edit raw application manifest JSON
            </Typography>
          </Box>
        </Box>
      </Card>
      
      {/* Enhanced Application Selection Card */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Typography
            variant="h6"
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: '#f9f9f9',
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <SearchIcon sx={{ mr: 1, color: 'primary.main' }} />
            Search & Select Application
          </Typography>
          
          <Box sx={{ p: 3 }}>
            <Autocomplete
              options={searchableApps}
              value={selectedSearchApp}
              onChange={handleEnhancedApplicationChange}
              getOptionLabel={(option) => option.displayName}
              filterOptions={filterApplications}
              loading={searchLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Applications"
                  placeholder="Type application name, client ID, or app type..."
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <SearchIcon sx={{ mr: 1, color: 'action.disabled' }} />
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: 'rgba(25, 118, 210, 0.5)',
                      },
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: 'transparent',
                        border: '1px solid rgba(0, 0, 0, 0.12)'
                      }}
                    >
                      {getAppTypeIcon(option.appType)}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {option.displayName}
                        </Typography>
                        <Chip
                          label={option.appType}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.75rem',
                            backgroundColor: getAudienceColor(option.signInAudience) + '20',
                            color: getAudienceColor(option.signInAudience),
                            fontWeight: 500
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {option.appId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          •
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.signInAudience}
                        </Typography>
                        {option.hasSecrets && (
                          <>
                            <Typography variant="caption" color="text.secondary">•</Typography>
                            <Chip
                              label="Has Secrets"
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: '0.625rem',
                                backgroundColor: '#f44336',
                                color: 'white'
                              }}
                            />
                          </>
                        )}
                        {option.hasCredentials && (
                          <>
                            <Typography variant="caption" color="text.secondary">•</Typography>
                            <Chip
                              label="Has Certificates"
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: '0.625rem',
                                backgroundColor: '#ff9800',
                                color: 'white'
                              }}
                            />
                          </>
                        )}
                      </Box>
                    }
                  />
                </Box>
              )}
              noOptionsText={
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No applications found matching your search.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Try searching by app name, client ID, or app type.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />

            {/* Search Tips Button - positioned below search bar */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              mb: selectedSearchApp ? 2 : 0
            }}>
              <Button 
                onClick={() => setShowSearchTips(!showSearchTips)}
                startIcon={<InfoOutlinedIcon fontSize="small" />}
                size="small"
                sx={{ 
                  textTransform: 'none',
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                {showSearchTips ? 'Hide Search Tips' : 'Show Search Tips'}
              </Button>
            </Box>

            {/* Collapsible Search Tips */}
            {showSearchTips && (
              <Box sx={{ 
                mb: selectedSearchApp ? 2 : 0,
                p: 2, 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #e9ecef',
                borderRadius: 1,
                borderLeft: '4px solid #17a2b8'
              }}>
                <Typography variant="subtitle2" sx={{ color: '#0c5460', mb: 1, fontWeight: 600 }}>
                  Search Tips:
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>By Name:</strong> "MyApp" or "Test Application"
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>By Client ID:</strong> "12345678-1234-1234-1234-123456789012"
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>By Type:</strong> "Web", "SPA", "API", "Mobile"
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Combined:</strong> "MyApp Web" or "API production"
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {selectedSearchApp && (
              <Alert 
                severity="info" 
                variant="outlined" 
                sx={{ borderRadius: 2 }}
                action={
                  <Button
                    color="primary"
                    size="small"
                    onClick={() => handleEnhancedApplicationChange({} as React.SyntheticEvent, selectedSearchApp)}
                    startIcon={<RefreshIcon />}
                    disabled={searchLoading}
                  >
                    {searchLoading ? 'Loading...' : 'Refresh Manifest'}
                  </Button>
                }
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Editing manifest for <strong>{selectedSearchApp.displayName}</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Client ID: <code>{selectedSearchApp.appId}</code>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">•</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Type: {selectedSearchApp.appType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">•</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Audience: {selectedSearchApp.signInAudience}
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Warning Alert - only show when an app is selected */}
      {selectedApp && (
        <Alert 
          severity="warning" 
          variant="filled"
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>Proceed with caution</AlertTitle>
          <Stack spacing={1}>
            <Typography variant="body2">
              Editing the raw application manifest can have serious consequences if done incorrectly:
            </Typography>
            
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <li>Consider creating a backup before making changes</li>
              <li>Changes take effect immediately when saved</li>
              <li>Incorrect JSON formatting can corrupt your application registration</li>
              <li>Removing critical properties may break application functionality</li>
            </Box>
          </Stack>
        </Alert>
      )}

      {/* Editor Card - only show when an app is selected */}
      {selectedApp ? (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
                backgroundColor: '#f9f9f9',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <CodeIcon sx={{ mr: 1, color: 'primary.main' }} />
              Manifest JSON
            </Typography>
            
            <Box sx={{ p: 3 }}>
              <TextField
                label="Application Manifest"
                variant="outlined"
                fullWidth
                multiline
                rows={20}
                value={manifestJson}
                onChange={(e) => setManifestJson(e.target.value)}
                placeholder='{"id":"application-id","appId":"client-id",...}'
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
              )}
              
              {successMessage && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{successMessage}</Alert>
              )}

              <Grid container spacing={2} justifyContent="space-between">
                <Grid item>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleValidate}
                      startIcon={<DoneIcon />}
                      sx={{ 
                        borderRadius: 2,
                        boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                      }}
                    >
                      Validate JSON
                    </Button>
                    
                    <Button
                      variant="outlined"
                      onClick={handleCopy}
                      startIcon={copySuccess ? <DoneIcon /> : <ContentCopyIcon />}
                      sx={{ borderRadius: 2 }}
                      color={copySuccess ? "success" : "primary"}
                    >
                      {copySuccess ? "Copied!" : "Copy"}
                    </Button>
                  </Stack>
                </Grid>
                
                <Grid item>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleReset}
                      startIcon={<RestoreIcon />}
                      sx={{ borderRadius: 2 }}
                      disabled={!selectedApp}
                    >
                      Reset
                    </Button>
                    
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveClick}
                      startIcon={<SaveIcon />}
                      sx={{ 
                        borderRadius: 2,
                        boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                          boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
                        }
                      }}
                      disabled={isLoading || !onSave}
                    >
                      {isLoading ? "Saving..." : "Save Changes"}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, bgcolor: '#f9f9f9' }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
            <Box 
              sx={{ 
                p: 2,
                borderRadius: '50%',
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <CodeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              No Application Selected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 450 }}>
              Please select an application from the dropdown above to edit its manifest.
              The manifest editor allows you to directly modify the application's configuration.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f9f9f9', mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">About Application Manifests</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(232, 245, 233, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#2e7d32' }}>
                  What is an Application Manifest?
                </Typography>
                <Typography variant="body2">
                  The application manifest is a JSON document that defines all aspects of an application 
                  registration in Microsoft Entra ID, including authentication settings, API permissions,
                  app roles, and more.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(227, 242, 253, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1976d2' }}>
                  Common Use Cases
                </Typography>
                <Typography variant="body2">
                  • Advanced property configuration not available in UI<br />
                  • Bulk application updates via scripts<br />
                  • CI/CD pipeline integration for app registrations<br />
                  • Templating and reusing configurations
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 243, 224, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#e65100' }}>
                  Best Practices
                </Typography>
                <Typography variant="body2">
                  • Create a backup before editing<br />
                  • Test changes in development environments first<br />
                  • Use Microsoft Graph API to export manifests<br />
                  • Validate JSON syntax before saving changes
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningAmberIcon sx={{ color: 'warning.main', mr: 1 }} />
          Confirm Manifest Update
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You are about to update the application manifest which will immediately change the 
            application registration. Incorrect changes may break your application functionality.
            <Box sx={{ mt: 2, fontWeight: 'bold' }}>
              Do you want to proceed with these changes?
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmOpen(false)} 
            startIcon={<CloseIcon />}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSave} 
            variant="contained" 
            color="warning"
            autoFocus
            startIcon={<SaveIcon />}
            sx={{ borderRadius: 2 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManifestEditor;